/* eslint-disable no-invalid-this, func-names */
/**
 * Side-effect module: registers custom Yup methods on the shared `yup` singleton.
 *
 * Import style (`import * as yup` vs default) does not matter for method availability.
 * What matters is that this file runs before any code that calls those methods — especially
 * schemas built at module load time (`const schema = yup.object({ ... requireOneOfFields })`).
 * The app entry `main.jsx` imports this first; Jest loads it via `jest.setup.js`. Any module
 * that defines such a schema should start with: `import "data/yup-validation";`
 */
import * as yup from "yup";
import { isPossibleUSPhoneNumber } from "utils/phone";

/**
 * Yup 1: `addMethod` must be applied to each concrete schema factory (`yup.string`,
 * `yup.date`, …). Registering only on `MixedSchema` leaves `StringSchema` instances
 * without the method (unlike Yup 0.32 where `mixed().constructor` was a shared base).
 */
const SCHEMA_TYPES_FOR_CROSS_FIELD_METHODS = [
  yup.string,
  yup.number,
  yup.date,
  yup.boolean,
  yup.mixed,
] as const;

const addCrossFieldMethod = function (
  name: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  implementation: (this: yup.AnySchema, ...args: any[]) => any,
): void {
  SCHEMA_TYPES_FOR_CROSS_FIELD_METHODS.forEach((schemaType) => {
    yup.addMethod(schemaType, name, implementation);
  });
};
const siblingFieldPresent = function (value: unknown): boolean {
  if (value === null || typeof value === "undefined") {
    return false;
  }
  if (typeof value === "string") {
    return value.trim() !== "";
  }
  if (typeof value === "boolean") {
    return value === true;
  }
  return true;
};

/**
 * Single source of truth for email validation across the app. Case-insensitive so
 * addresses entered with mixed case (e.g. `John.Doe@Example.com`) are accepted, and
 * paired with normalization (trim + lowercase) so emails are processed consistently.
 */
export const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/i;

export const normalizeEmail = (value: string): string => value.trim().toLowerCase();

export const isValidEmail = (value: string): boolean => EMAIL_REGEX.test(normalizeEmail(value));

// Must be `interface` (not `type`) — TypeScript only merges augmentations via interfaces.
declare module "yup" {
  // eslint-disable-next-line @typescript-eslint/consistent-type-definitions -- declaration merging
  interface StringSchema {
    requireOneOfFields(fields: string[], errorMsg: string): StringSchema;
    requireSibling(field: string, errorMsg?: string): StringSchema;
    validPhoneNumber(errorMsg?: string): StringSchema;
    validUSPhoneNumber(errorMsg?: string): StringSchema;
    validSSN(errorMsg?: string): StringSchema;
    validPassword(errorMsg?: string): StringSchema;
    validEmail(errorMsg?: string): StringSchema;
  }
  // eslint-disable-next-line @typescript-eslint/consistent-type-definitions -- declaration merging
  interface DateSchema {
    requireOneOfFields(fields: string[], errorMsg: string): DateSchema;
  }
  // eslint-disable-next-line @typescript-eslint/consistent-type-definitions -- declaration merging
  interface BooleanSchema {
    requireOneOfFields(fields: string[], errorMsg: string): BooleanSchema;
    toggleSibling(field: string, errorMsg?: string): BooleanSchema;
  }
}

// setup default messages for commonly used validations
yup.setLocale({
  mixed: {
    default: "It is not valid",
    required: "Required field",
  },
  string: {
    email: "Valid email required",
    min: "Must be at least ${min} characters",
    max: "Must be at most ${max} characters",
  },
  number: {
    min: "Must be at least ${min} or greater",
  },
});

addCrossFieldMethod("requireOneOfFields", function (this: yup.AnySchema, fields: string[], msg: string) {
  return this.test({
    name: "one-field-required",
    message: msg,
    skipAbsent: false,
    test (_value, { parent }) {
      return fields.some((fieldName) =>
        siblingFieldPresent(parent[fieldName]));
    },
  });
});

addCrossFieldMethod("requireSibling", function (this: yup.AnySchema, sibName: string, msg = "Required field") {
  return this.test({
    name: "sibling-required",
    message: msg,
    skipAbsent: false,
    test (value, { parent }) {
      if (siblingFieldPresent(parent[sibName])) {
        return !!value;
      }
      return true;
    },
  });
});

yup.addMethod(yup.string, "validPhoneNumber", function (msg = "Invalid phone") {
  return this.test("valid-us-phone", msg, (value) => {
    if (value) {
      return (value || "").replace(/[^0-9]/g, "").length === 10;
    }
    return true;
  });
});
yup.addMethod(yup.string, "validUSPhoneNumber", function (msg = "Invalid phone") {
  return this.test("valid-us-national-phone", msg, (value) => {
    if (value) {
      return isPossibleUSPhoneNumber(value);
    }
    return true;
  });
});

yup.addMethod(yup.string, "validEmail", function (msg = "Invalid email format") {
  return this
    .transform((value) => (typeof value === "string" ? normalizeEmail(value) : value))
    .matches(EMAIL_REGEX, { message: msg, excludeEmptyString: true });
});

yup.addMethod(yup.string, "validSSN", function (msg = "Invalid SSN") {
  return this.test("valid-ssn", msg, (value) => {
    if (value) {
      return (value || "").replace(/[^0-9]/g, "").length === 9;
    }
    return true;
  });
});

addCrossFieldMethod("toggleSibling", function (this: yup.AnySchema, sibName: string, msg = "Cannot select both") {
  return this.test({
    name: "toggle-sibling",
    message: msg,
    skipAbsent: false,
    test (value, { parent }) {
      return !(parent[sibName] && value);
    },
  });
});

const passMsg =
  "Password must contain one lower case letter, one uppercase letter," +
  " one number, and a minimum of 8 characters total";

yup.addMethod(yup.string, "validPassword", function (msg = passMsg) {
  return this.test("valid-password", msg, (value) => {
    if (value) {
      return (value.length >= 8 && value.match(/[0-9]/) &&
        value.match(/[a-z]/) && value.match(/[A-Z]/)) as boolean;
    }
    return true;
  });
});
